import React from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { useSelector, useDispatch } from 'react-redux';
import { graphsBackgroundColor } from '../../../constants/Misc';
import { fetchPressureTransients, fetchPressureSettings } from '../../../actions/SensorsPressureActions';
import { getTransientSeverity, TRANSIENT_SEVERITY_COLORS } from '../Pressure/pressureTraceUtils';

require('./SensorInfo.scss');

const PRESSURE_CONVERSION = {
  meter: { factor: 1, label: 'Meter' },
  bar: { factor: 0.0980665, label: 'Bar' },
  kpa: { factor: 9.80665, label: 'kPa' },
  psi: { factor: 1.42233, label: 'PSI' },
};

const GRAPH_WIDTH = 330;
const GRAPH_HEIGHT = 220;
const GRAPH_MARGIN = { l: 65, r: 20, b: 60, t: 10 };
const FONT_SIZE_TITLE = 12;
const FONT_SIZE_TICK = 10;
const FONT_SIZE_TEXT = 10;
const THREE_DAYS_MS = 86400000 * 3;
const ONE_HOUR_MS = 3600000;

const COLORS = {
  alarm: 'red',
  trendAvg: 'rgb(0, 100, 200)',
  trendMax: 'rgb(0, 150, 0)',
  trendMin: 'rgb(255, 140, 0)',
  configured: 'rgb(220, 20, 20)',
  thresholdPink: 'rgb(255, 182, 193)',
};

const PLOT_CONFIG = {
  displaylogo: false,
  responsive: true,
  displayModeBar: true,
  modeBarButtonsToRemove: ['drawrect', 'zoomIn2d', 'zoomOut2d', 'resetScale2d', 'select2d', 'lasso2d', 'toggleSpikelines', 'resetViewMapbox'],
};

const parseValue = (value) => (value != null ? parseFloat(value) : null);

const parseDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return !isNaN(date.getTime()) ? date : null;
};

const createTrendTrace = (dates, values, name, color) => ({
  x: dates,
  y: values,
  mode: 'lines+markers',
  type: 'scatter',
  name,
  line: { color, width: 2 },
  marker: { color, size: 6, opacity: 0.9, line: { color: 'white', width: 1 } },
});

const createConfiguredLine = (dates, value, name) => ({
  x: [dates[0], dates[dates.length - 1]],
  y: [value, value],
  mode: 'lines',
  type: 'scatter',
  name,
  line: { color: COLORS.configured, width: 2.5, dash: 'dash' },
  showlegend: true,
  hoverinfo: 'y+name',
});

const createThresholdLine = (minDate, maxDate, value, name, color = COLORS.configured) => ({
  x: [minDate, maxDate],
  y: [value, value],
  mode: 'lines',
  type: 'scatter',
  name,
  line: { color, width: 2, dash: 'dash' },
  showlegend: false,
  hoverinfo: 'y+name',
});

// Minimum Y-axis range equivalent to 3 bar, converted into the currently displayed unit.
const MIN_RANGE_BAR = 3;
const getMinRangeInUnit = (conversion) => MIN_RANGE_BAR * (conversion.factor / PRESSURE_CONVERSION.bar.factor);

// If the data range is smaller than the min range, return a yaxis { range, autorange: false }
// override centered on the data; otherwise return an empty object to keep autorange.
const buildYAxisMinRange = (values, conversion) => {
  const nums = values.filter((v) => v != null && !isNaN(v));
  if (!nums.length) return {};
  const yMin = Math.min(...nums);
  const yMax = Math.max(...nums);
  const minRange = getMinRangeInUnit(conversion);
  if (yMax - yMin >= minRange) return {};
  const center = (yMin + yMax) / 2;
  return { range: [center - minRange / 2, center + minRange / 2], autorange: false };
};

const ensureMinDaysRange = (minDate, maxDate, minDays = 7) => {
  const daysDiff = (maxDate - minDate) / (1000 * 60 * 60 * 24);
  if (daysDiff < minDays) {
    const daysToAdd = Math.ceil((minDays - daysDiff) / 2);
    minDate.setDate(minDate.getDate() - daysToAdd);
    // maxDate.setDate(maxDate.getDate() + daysToAdd);
  }
  return { minDate, maxDate };
};

const PressureSensorSampleStrip = ({ sensorDetails, onClose, context, samplesCount, reloadSamples }) => {
  const dispatch = useDispatch();
  const [input, setInput] = React.useState(samplesCount || 20);
  const [traceVisibility, setTraceVisibility] = React.useState({});

  const selectedProject = useSelector((state) => state.leaksList?.selectedProject);
  const samples = useSelector((state) => {
    const project = state.leaksList?.selectedProject;
    return state.leaksList.leaksByProject?.[project]?.sensors?.pressureSensors?.samples || [];
  });
  const pressureAlarms = useSelector((state) => {
    const project = state.leaksList?.selectedProject;
    return state.leaksList.leaksByProject?.[project]?.sensors?.pressureSensors?.alarms || [];
  });
  const transients = useSelector((state) => state.leaksList.pressure?.transientsData?.data || []);
  const transientThresholds = useSelector((state) => state.leaksList.pressure?.transientThresholds || { med: 20, high: 30 });
  const pressureUnit = useSelector((state) => state.local.pressureUnit || 'bar');

  const conversion = PRESSURE_CONVERSION[pressureUnit] || PRESSURE_CONVERSION.meter;
  const sensorId = pressureAlarms?.[0]?.pressure_sensor_id || null;

  React.useEffect(() => {
    if (selectedProject && sensorId) {
      dispatch(fetchPressureTransients(selectedProject, sensorId));
      dispatch(fetchPressureSettings(selectedProject));
    }
  }, [dispatch, selectedProject, sensorId]);

  const handleChange = (event) => setInput(Math.min(event.target.value, 60));
  const handleLoadClick = () => reloadSamples?.(input);

  const handleLegendClick = (event) => {
    if (event?.data && event.curveNumber !== undefined) {
      const traceName = event.data[event.curveNumber]?.name;
      if (traceName) {
        setTraceVisibility((prev) => ({
          ...prev,
          [traceName]: prev[traceName] === 'legendonly' ? true : 'legendonly',
        }));
      }
    }
    return false;
  };

  if (!Array.isArray(sensorDetails?.barsData) || sensorDetails.barsData.length !== 3) {
    return null;
  }

  const getConfiguredValues = () => {
    if (!samples?.length) return { configuredMin: null, configuredMax: null };
    const sample = samples[0];
    const confMin = parseValue(sample?.configured_min_m);
    const confMax = parseValue(sample?.configured_max_m);
    return {
      configuredMin: confMin != null ? confMin * conversion.factor : null,
      configuredMax: confMax != null ? confMax * conversion.factor : null,
    };
  };

  const renderScatterGraph = () => {
    if (!samples?.length) return null;

    const dates = [];
    const trendAvg = [];
    const trendMax = [];
    const trendMin = [];
    let configuredMin = null;
    let configuredMax = null;

    samples.forEach((sample) => {
      dates.push(parseDate(sample.sample_date) || dates.length);
      const avgVal = parseValue(sample.trend_avg_m);
      const maxVal = parseValue(sample.trend_max_m);
      const minVal = parseValue(sample.trend_min_m);
      trendAvg.push(avgVal != null ? avgVal * conversion.factor : null);
      trendMax.push(maxVal != null ? maxVal * conversion.factor : null);
      trendMin.push(minVal != null ? minVal * conversion.factor : null);
      if (configuredMin === null) {
        const confMin = parseValue(sample.configured_min_m);
        configuredMin = confMin != null ? confMin * conversion.factor : null;
      }
      if (configuredMax === null) {
        const confMax = parseValue(sample.configured_max_m);
        configuredMax = confMax != null ? confMax * conversion.factor : null;
      }
    });

    if (!dates.length) return null;

    const getVisibility = (name) => traceVisibility[name] || true;

    const scatterData = [
      { ...createTrendTrace(dates, trendAvg, context.t('trend_avg'), COLORS.trendAvg), visible: getVisibility(context.t('trend_avg')) },
      { ...createTrendTrace(dates, trendMax, context.t('trend_max'), COLORS.trendMax), visible: getVisibility(context.t('trend_max')) },
      { ...createTrendTrace(dates, trendMin, context.t('trend_min'), COLORS.trendMin), visible: getVisibility(context.t('trend_min')) },
    ];

    if (configuredMin != null) {
      scatterData.push({ ...createConfiguredLine(dates, configuredMin, context.t('configured_min')), visible: getVisibility(context.t('configured_min')) });
    }
    if (configuredMax != null) {
      scatterData.push({ ...createConfiguredLine(dates, configuredMax, context.t('configured_max')), visible: getVisibility(context.t('configured_max')) });
    }

    const scatterYOverride = buildYAxisMinRange(
      [...trendAvg, ...trendMax, ...trendMin, configuredMin, configuredMax],
      conversion,
    );

    return (
      <Plot
        data={scatterData}
        layout={{
          paper_bgcolor: graphsBackgroundColor,
          plot_bgcolor: '#ffffff',
          width: GRAPH_WIDTH,
          height: GRAPH_HEIGHT,
          margin: GRAPH_MARGIN,
          hovermode: 'closest',
          xaxis: {
            title: { text: 'Min/Max/Avg', font: { color: '#333', size: FONT_SIZE_TITLE } },
            tickfont: { size: FONT_SIZE_TICK, color: '#333' },
            type: 'date',
            gridcolor: 'rgba(200, 200, 200, 0.3)',
            showgrid: true,
            tickangle: -45,
          },
          yaxis: {
            title: { text: conversion.label, font: { color: '#333', size: FONT_SIZE_TITLE } },
            tickfont: { size: FONT_SIZE_TICK, color: '#333' },
            gridcolor: 'rgba(200, 200, 200, 0.3)',
            showgrid: true,
            ...scatterYOverride,
          },
          legend: {
            orientation: 'v',
            x: 1.02,
            y: 1,
            xanchor: 'left',
            yanchor: 'top',
            font: { size: FONT_SIZE_TEXT, color: '#333' },
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            bordercolor: '#ccc',
            borderwidth: 1,
          },
          showlegend: false,
        }}
        config={PLOT_CONFIG}
        onLegendClick={handleLegendClick}
      />
    );
  };

  const renderAlarmsGraph = () => {
    const traces = [];
    const { configuredMin, configuredMax } = getConfiguredValues();

    let minDate = new Date();
    let maxDate = new Date();
    minDate.setDate(minDate.getDate() - 7);

    if (pressureAlarms?.length > 0) {
      const alarmDates = pressureAlarms.map((alarm) => new Date(alarm.alarm_time));
      minDate = new Date(Math.min(...alarmDates));
      // maxDate = new Date(Math.max(...alarmDates));
      minDate.setDate(minDate.getDate() - 1);
      // maxDate.setDate(maxDate.getDate() + 1);
      ensureMinDaysRange(minDate, maxDate, 7);

      traces.push({
        x: pressureAlarms.map((alarm) => new Date(alarm.alarm_time)),
        y: pressureAlarms.map((alarm) => parseValue(alarm.value_m) * conversion.factor),
        mode: 'markers',
        type: 'scatter',
        name: context.t('alarms'),
        marker: { color: 'red', size: 10, symbol: 'circle' },
        showlegend: false,
        hovertemplate: '%{x|%Y-%m-%d %H:%M}<br>Value: %{y:.2f}<extra></extra>',
      });
    }

    if (configuredMax != null) traces.push(createThresholdLine(minDate, maxDate, configuredMax, context.t('configured_max')));
    if (configuredMin != null) traces.push(createThresholdLine(minDate, maxDate, configuredMin, context.t('configured_min')));

    const alarmYValues = [
      ...(pressureAlarms?.map((a) => parseValue(a.value_m) * conversion.factor) || []),
      configuredMin,
      configuredMax,
    ];
    const alarmYOverride = buildYAxisMinRange(alarmYValues, conversion);

    return (
      <Plot
        data={traces}
        layout={{
          paper_bgcolor: graphsBackgroundColor,
          plot_bgcolor: '#ffffff',
          width: GRAPH_WIDTH,
          height: GRAPH_HEIGHT,
          margin: GRAPH_MARGIN,
          hovermode: 'closest',
          xaxis: {
            title: { text: context.t('alarms'), font: { color: '#333', size: FONT_SIZE_TITLE } },
            tickfont: { size: FONT_SIZE_TICK, color: '#333' },
            type: 'date',
            gridcolor: 'rgba(200, 200, 200, 0.3)',
            showgrid: true,
            tickangle: -45,
            range: [minDate, maxDate],
            nticks: 7,
            tickformat: '%b %d',
          },
          yaxis: {
            title: { text: conversion.label, font: { color: '#333', size: FONT_SIZE_TITLE } },
            tickfont: { size: FONT_SIZE_TICK, color: '#333' },
            gridcolor: 'rgba(200, 200, 200, 0.3)',
            showgrid: true,
            ...alarmYOverride,
          },
          showlegend: false,
        }}
        config={PLOT_CONFIG}
      />
    );
  };

  const renderTransientsGraph = () => {
    const { configuredMin, configuredMax } = getConfiguredValues();
    const traces = [];

    let minDate = new Date();
    let maxDate = new Date();
    minDate.setDate(minDate.getDate() - 7);

    if (!transients?.length) {
      if (configuredMax != null) traces.push(createThresholdLine(minDate, maxDate, configuredMax, context.t('configured_max'), COLORS.thresholdPink));
      if (configuredMin != null) traces.push(createThresholdLine(minDate, maxDate, configuredMin, context.t('configured_min'), COLORS.thresholdPink));

      const emptyYOverride = buildYAxisMinRange([configuredMin, configuredMax], conversion);

      return (
        <Plot
          data={traces}
          layout={{
            paper_bgcolor: graphsBackgroundColor,
            plot_bgcolor: '#ffffff',
            width: GRAPH_WIDTH,
            height: GRAPH_HEIGHT,
            margin: GRAPH_MARGIN,
            xaxis: {
              title: { text: context.t('transients'), font: { color: '#333', size: FONT_SIZE_TITLE } },
              tickfont: { size: FONT_SIZE_TICK, color: '#333' },
              type: 'date',
              range: [minDate, maxDate],
              dtick: THREE_DAYS_MS,
              tickformat: '%b %d',
            },
            yaxis: {
              title: { text: conversion.label, font: { color: '#333', size: FONT_SIZE_TITLE } },
              tickfont: { size: FONT_SIZE_TICK, color: '#333' },
              ...emptyYOverride,
            },
            showlegend: false,
          }}
          config={{ displaylogo: false, responsive: true, displayModeBar: true }}
        />
      );
    }

    const transientData = transients
      .map((item) => {
        const minVal = parseValue(item.min_reading);
        const maxVal = parseValue(item.max_reading);
        return { ...item, minVal, maxVal, height: maxVal != null && minVal != null ? maxVal - minVal : 0 };
      })
      .filter((item) => item.minVal != null && item.maxVal != null);

    const transientDates = transientData.map((t) => new Date(t.event_time));
    minDate = new Date(Math.min(...transientDates));
    // maxDate = new Date(Math.max(...transientDates));
    minDate.setDate(minDate.getDate() - 1);
    // maxDate.setDate(maxDate.getDate() + 1);
    ensureMinDaysRange(minDate, maxDate, 7);

    transientData.forEach((item) => {
      const color = TRANSIENT_SEVERITY_COLORS[getTransientSeverity(item.height, transientThresholds)];
      const date = new Date(item.event_time);
      const minConverted = item.minVal * conversion.factor;
      const maxConverted = item.maxVal * conversion.factor;
      const midPoint = (minConverted + maxConverted) / 2;
      const bodyHeight = (maxConverted - minConverted) * 0.6;

      // Wick
      traces.push({ x: [date, date], y: [minConverted, maxConverted], mode: 'lines', type: 'scatter', line: { color, width: 2 }, showlegend: false, hoverinfo: 'skip' });
      // Body
      const dateLabel = `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      traces.push({ x: [date, date], y: [midPoint - bodyHeight / 2, midPoint + bodyHeight / 2], mode: 'lines', type: 'scatter', line: { color, width: 8 }, showlegend: false, hoverinfo: 'text', text: [`${dateLabel}<br>Min: ${minConverted.toFixed(2)}<br>Max: ${maxConverted.toFixed(2)}`] });
      // Top cap
      traces.push({ x: [new Date(date.getTime() - ONE_HOUR_MS), new Date(date.getTime() + ONE_HOUR_MS)], y: [maxConverted, maxConverted], mode: 'lines', type: 'scatter', line: { color, width: 2 }, showlegend: false, hoverinfo: 'skip' });
      // Bottom cap
      traces.push({ x: [new Date(date.getTime() - ONE_HOUR_MS), new Date(date.getTime() + ONE_HOUR_MS)], y: [minConverted, minConverted], mode: 'lines', type: 'scatter', line: { color, width: 2 }, showlegend: false, hoverinfo: 'skip' });
    });

    if (configuredMax != null) traces.push(createThresholdLine(minDate, maxDate, configuredMax, context.t('configured_max'), COLORS.thresholdPink));
    if (configuredMin != null) traces.push(createThresholdLine(minDate, maxDate, configuredMin, context.t('configured_min'), COLORS.thresholdPink));

    const transientYValues = transientData.flatMap((item) => [
      item.minVal * conversion.factor,
      item.maxVal * conversion.factor,
    ]);
    const transientYOverride = buildYAxisMinRange([...transientYValues, configuredMin, configuredMax], conversion);

    return (
      <Plot
        data={traces}
        layout={{
          paper_bgcolor: graphsBackgroundColor,
          plot_bgcolor: '#ffffff',
          width: GRAPH_WIDTH,
          height: GRAPH_HEIGHT,
          margin: GRAPH_MARGIN,
          hovermode: 'closest',
          xaxis: {
            title: { text: context.t('transients'), font: { color: '#333', size: FONT_SIZE_TITLE } },
            tickfont: { size: FONT_SIZE_TICK, color: '#333' },
            type: 'date',
            gridcolor: 'rgba(200, 200, 200, 0.3)',
            showgrid: true,
            tickangle: -45,
            range: [minDate, maxDate],
            dtick: THREE_DAYS_MS,
            tickformat: '%b %d',
          },
          yaxis: {
            title: { text: conversion.label, font: { color: '#333', size: FONT_SIZE_TITLE } },
            tickfont: { size: FONT_SIZE_TICK, color: '#333' },
            gridcolor: 'rgba(200, 200, 200, 0.3)',
            showgrid: true,
            ...transientYOverride,
          },
          showlegend: false,
        }}
        config={PLOT_CONFIG}
      />
    );
  };

  return (
    <div className="sample-info">
      <div>
        <i className="icon-close" onClick={onClose} />
        <div className="reload-noises-parent">
          <label>{context.t('number_of_samples')}:</label>
          <input
            className="input-field"
            type="number"
            placeholder={context.t('number_of_samples')}
            min="0"
            max="60"
            onChange={handleChange}
            value={input}
          />
          <button className="btn-reload-noises" onClick={handleLoadClick}>
            {context.t('load')}
          </button>
        </div>

        <div className="sample-graphs">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {renderScatterGraph()}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {renderAlarmsGraph()}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {renderTransientsGraph()}
          </div>
        </div>
      </div>
    </div>
  );
};

PressureSensorSampleStrip.propTypes = {
  sensorDetails: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  context: PropTypes.object.isRequired,
  samplesCount: PropTypes.number,
  reloadSamples: PropTypes.func,
};

PressureSensorSampleStrip.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default PressureSensorSampleStrip;
